import {ColoredOption} from '@/types/ColoredOption'

/**
 * O que toda escada de níveis tem igual.
 *
 * Existem **duas** no cliente, e elas são a mesma conta com valores diferentes: o nível de acesso de
 * alguém dentro de um ambiente (`types/AccessLevel.ts`) e a admissão de uma conta no lunedi
 * (`types/Access.ts`). Escrita duas vezes, bastaria uma delas trocar um `>=` por um `>` para as
 * duas discordarem sobre o que "alcança" quer dizer — e a segunda cópia é o momento de extrair.
 *
 * A ordem do array É a regra: cada degrau alcança a si mesmo e a todos os anteriores.
 */

/**
 * O nível que se tem alcança o que se exige?
 *
 * **Nível ausente ou desconhecido não alcança nada.** `undefined` chega aqui quando a resposta não
 * existe — a conta que nunca sincronizou, o ambiente que veio de um servidor antigo —, e tratá-lo
 * como o degrau mais baixo faria a tela desenhar de mais. Desenhar de menos é o lado seguro.
 */
export function reaches<T extends string>(order: readonly T[], have: T | undefined, needed: T): boolean {
    const position = order.indexOf(have as T)
    return position >= 0 && position >= order.indexOf(needed)
}

/**
 * Um degrau como valor colorido, para desenhar com `form: 'chip'`.
 *
 * O `id` é a posição na escada, que é o que o `ColoredOption` pede e também a ordem natural em
 * que os níveis se listam.
 */
export function optionOf<T extends string>(order: readonly T[], color: Record<T, string>,
                                           name: (level: T) => string, level: T): ColoredOption {
    return {id: order.indexOf(level), name: name(level), color: color[level]}
}

/** Todos os degraus para escolher, do mais fraco ao mais forte. */
export function optionsOf<T extends string>(order: readonly T[], color: Record<T, string>,
                                            name: (level: T) => string): Array<ColoredOption & {value: T}> {
    return order.map(level => ({...optionOf(order, color, name, level), value: level}))
}
